import moment from "moment";
import sessionHandling from "services/utils/notificationUtils";
import { apiErrorText, shiftRequest } from "../ShiftEarnings/shiftEarningsApi";

const BASE = "payroll/earning/overtime/";

// Durations are decimal hours for totals; form quantities retain HH:MM.
export const numericQuantity = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const text = String(value).trim();
  if (/^\d+:[0-5]\d$/.test(text)) {
    const [hours, minutes] = text.split(":").map(Number);
    return hours + minutes / 60;
  }
  const number = Number(text);
  return Number.isFinite(number) ? number : null;
};

// A quantity as the API sent it: "HH:MM" stays as is, a number gets 2
// decimals, missing is "–".
const DURATION = /^\d+:[0-5]\d$/;
export const isDuration = (value) => value != null && DURATION.test(String(value).trim());
// Amount basis: the entry is a fixed amount, so no rate applies (list and
// view show "–" for Rate).
export const isAmountBasis = (row) => String(row?.basis ?? row?.based_on ?? "").trim().toLowerCase() === "amount";
export const formatRawQuantity = (raw, numeric) => {
  if (isDuration(raw)) return String(raw).trim();
  return numeric == null || !Number.isFinite(Number(numeric)) ? "–" : Number(numeric).toFixed(2);
};
// Card total: every row a duration -> "HH:MM", else a decimal sum.
export const sumRawQuantity = (rows) => {
  const withValue = rows.filter((row) => row.quantity != null);
  const total = withValue.reduce((acc, row) => acc + Number(row.quantity), 0);
  if (withValue.length && withValue.every((row) => isDuration(row.raw_quantity))) {
    const minutes = Math.round(total * 60);
    return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
  }
  return (Math.round(total * 100) / 100).toFixed(2);
};

export const fromOvertimeApi = (row) => {
  const date = moment(row.date, ["DD-MM-YYYY", "YYYY-MM-DD"], true);
  // Credited Qty is the final approved OT ("08:00"); the older keys are
  // read only when it is missing.
  const quantity = row.final_approved_ot ?? row.credited_quantity ?? row.quantity;
  return {
    ...row,
    date: date.isValid() ? date.format("YYYY-MM-DD") : row.date,
    basis: row.based_on ?? row.basis,
    quantity: numericQuantity(quantity),
    raw_quantity: quantity,
    ot_hours: numericQuantity(row.final_approved_ot),
    worked_hours: numericQuantity(row.ot_working_hours),
    penalty: numericQuantity(row.penalty),
    net: numericQuantity(row.net),
    adjusted: numericQuantity(row.adjusted),
    // The API now returns the rate as `rate`; per_day / per_hour (the old
    // keys) are read only if it is missing.
    rate: row.rate ?? row.per_day ?? row.per_hour,
    payable_amount: row.earning_amount ?? row.amount,
    pay_schedule_id: row.pay_schedule,
    payroll_cycle: row.pay_schedule_name || "Unassigned",
  };
};

export const toOvertimePayload = (form, creating = false) => ({
  ...(creating ? { employee_id: form.employee_code } : {}),
  date: form.date,
  basis: form.basis,
  quantity: form.basis === "hours" && String(form.quantity).includes(":") ? form.quantity.trim() : Number(form.quantity),
  rate: Number(form.rate),
  ...(form.band_name ? { band_name: form.band_name.trim() } : {}),
  ...(String(form.payment_factor).trim() ? { factor: Number(form.payment_factor) } : {}),
  ...(creating && !form.shift_name ? {} : { shift: form.shift_name || null }),
  ...(form.pay_schedule_id === "" ? (creating ? {} : { pay_schedule_id: null }) : { pay_schedule_id: Number(form.pay_schedule_id) }),
  ...(!creating ? { status: form.status } : {}),
});

export const listOvertime = async (domain, companyId, period, signal) => {
  const params = new URLSearchParams({ company_id: companyId, start_date: period.start, end_date: period.end });
  const data = await shiftRequest(domain, `${BASE}earning_details/?${params}`, { signal });
  if (!Array.isArray(data)) throw new Error("The server returned an invalid overtime list.");
  return data.filter((row) => !row.isdeleted).map(fromOvertimeApi);
};

export const saveOvertime = async (domain, form, id, signal, initial) => {
  const payload = toOvertimePayload(form, id == null);
  const before = initial ? toOvertimePayload(initial) : null;
  const body = before ? Object.fromEntries(Object.entries(payload).filter(([key, value]) => value !== before[key])) : payload;
  const row = await shiftRequest(domain, `${BASE}earning/${id == null ? "" : `${id}/`}`, { method: id == null ? "POST" : "PUT", body, signal });
  if (row?.id == null) throw new Error("The server returned an invalid overtime earning.");
  return fromOvertimeApi(row);
};

export const deleteOvertime = (domain, id, signal) => shiftRequest(domain, `${BASE}earning/${id}/`, { method: "DELETE", signal });
export const bulkOvertime = async (domain, ids, changes, signal) => {
  const data = await shiftRequest(domain, `${BASE}earning/bulk-action/`, { method: "PUT", body: { ids, ...changes }, signal });
  if (!Array.isArray(data?.updated) || !Array.isArray(data?.errors)) throw new Error("The server returned an invalid bulk action result. Refresh the list before trying again.");
  return data;
};

// Overtime Bulk Import API. payScheduleId is optional: when sent, every row
// gets that schedule as-is (its date need not be inside it); when left out,
// each row's schedule comes from the employee's payroll group + the row's
// date. Answers { total, success, failed, rows: [{ employee_id,
// employee_name, date, band_name, status: "success" | "failed", message }] };
// HTTP 400 when every row failed still carries that body, so it is returned
// as a result, not thrown.
export const importOvertime = async (domain, payScheduleId, records, signal) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${BASE}import/`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    body: JSON.stringify({ ...(payScheduleId ? { pay_schedule_id: Number(payScheduleId) } : {}), records }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 409) sessionHandling();
  const data = await response.json().catch(() => null);
  if (Array.isArray(data?.rows)) return data;
  if (!response.ok) throw new Error(apiErrorText(data?.error || data?.detail || data) || `Import failed (${response.status}). Please try again.`);
  throw new Error("The server returned an invalid import result. Refresh the list before trying again.");
};

// Checks the records like the import does, without saving (same contract as
// Shift Earnings' import/validate/: { total, valid, failed, rows: [{ index,
// status: "valid" | "failed", message }] }, index = position in `records`).
// The Overtime Bulk Import API doc says this endpoint doesn't exist yet, so a
// 404 / 405 returns null and the wizard keeps its browser checks only.
export const validateOvertimeImport = async (domain, payScheduleId, records, signal) => {
  const response = await fetch(`${domain.replace(/\/$/, "")}/${BASE}import/validate/`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    body: JSON.stringify({ ...(payScheduleId ? { pay_schedule_id: Number(payScheduleId) } : {}), records }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  if (response.status === 409) sessionHandling();
  const data = await response.json().catch(() => null);
  if (Array.isArray(data?.rows)) return data;
  // Endpoint not there (no body, or DRF's {"detail": "Not found."}). A 404
  // with an `error` message is a real answer, e.g. "payroll schedule not found".
  if (response.status === 405 || (response.status === 404 && !data?.error)) return null;
  if (!response.ok) throw new Error(apiErrorText(data?.error || data?.detail || data) || `Validation failed (${response.status}). Please try again.`);
  throw new Error("The server returned an invalid validation result.");
};

export const emptyOvertimeForm = () => ({ employee_code: "", date: moment().format("YYYY-MM-DD"), basis: "hours", quantity: "", rate: "", band_name: "", payment_factor: "", shift_name: "", pay_schedule_id: "", status: "Pending" });
export const overtimeRowToForm = (row) => ({
  employee_code: row.employee_code, employee_name: row.employee_name,
  // Employee card on Edit (the GET API now returns these, like Shift Earnings).
  image_url: row.image_url || "", department_name: row.department_name || row.department || "",
  designation: row.designation_name || row.designation || "",
  date: row.date, basis: row.basis || "hours", quantity: String(row.raw_quantity ?? ""),
  rate: String(row.rate ?? ""), band_name: row.band_name || "", payment_factor: String(row.payment_factor ?? ""),
  shift_name: row.shift_name || "", pay_schedule_id: row.pay_schedule_id == null ? "" : String(row.pay_schedule_id),
  status: row.status, payroll_cycle: row.payroll_cycle,
});

export const validateOvertime = (form, initial) => {
  const errors = {};
  if (!form.employee_code) errors.employee_code = "Select an employee.";
  if (!moment(form.date, "YYYY-MM-DD", true).isValid()) errors.date = "Enter a valid date.";
  if (!["shift", "hours"].includes(form.basis)) errors.basis = "Select a basis.";
  const quantity = form.basis === "hours" ? numericQuantity(form.quantity) : (String(form.quantity).trim() === "" ? null : Number(form.quantity));
  if (quantity == null || !Number.isFinite(quantity) || quantity < 0) errors.quantity = "Enter a quantity of 0 or more (HH:MM or decimal hours for Hours).";
  for (const key of ["rate", "payment_factor"]) {
    if (key === "payment_factor" && !String(form[key]).trim()) continue;
    if (!String(form[key]).trim() || !Number.isFinite(Number(form[key])) || Number(form[key]) < 0) errors[key] = "Enter a number of 0 or more.";
  }
  if (initial) Object.keys(errors).forEach((key) => { if (form[key] === initial[key]) delete errors[key]; });
  return errors;
};
